<template>
    <div class="cm">
        <template v-if="!row">
            <span v-if="loading" class="cm__muted">Measuring…</span>
            <span v-else class="cm__muted">{{ unavailableText }}</span>
        </template>
        <template v-else-if="!row.tracked">
            <span class="cm__muted">Not tracked in this project</span>
            <router-link v-if="projectId" class="cm__link" :to="promptsRoute" :aria-label="'Add to tracked prompts: ' + promptText">
                Add to tracked prompts
                <span aria-hidden="true">→</span>
            </router-link>
        </template>
        <template v-else-if="!row.answers">
            <span class="cm__muted">Tracked, no AI answers in the last {{ days }} days</span>
        </template>
        <template v-else>
            <div class="cm__stats">
                <span class="cm__stat" :class="row.cited ? 'cm__stat--good' : 'cm__stat--bad'" :title="citedTitle">
                    <v-icon x-small class="mr-1" aria-hidden="true">mdi-format-quote-close</v-icon>
                    cited in {{ row.cited }} of {{ row.answers }}
                </span>
                <span v-if="row.mentioned !== null" class="cm__stat" :class="row.mentioned ? 'cm__stat--good' : 'cm__stat--warn'" :title="mentionedTitle">
                    <v-icon x-small class="mr-1" aria-hidden="true">mdi-tag-outline</v-icon>
                    brand in {{ row.mentioned }} of {{ row.answers }}
                </span>
            </div>
            <span class="cm__platforms">{{ answersLine }}</span>
            <router-link v-if="projectId" class="cm__link" :to="answersRoute" :aria-label="'View answers for: ' + promptText">
                View answers
                <span aria-hidden="true">→</span>
            </router-link>
        </template>
    </div>
</template>

<script>
    // Measured visibility of ONE prompt text next to its content-fit verdict: how many AI
    // answers the linked project collected for it, how many cite the audited URL and how
    // many mention the own brand. Rows come from POST /ai-suite/audit/coverage/tracking;
    // the parent passes the matching row (or null while it loads / when there is none).
    // Links are real router links (keyboard reachable, named per prompt for screen readers).
    import { platformLabel } from "./coverageText"

    export default {
        name: "CoverageMeasured",
        props: {
            row: { type: Object, default: null },
            loading: { type: Boolean, default: false },
            days: { type: Number, default: 90 },
            projectId: { type: String, default: "" },
            // shown when there is no row and nothing is loading (e.g. the request failed)
            unavailableText: { type: String, default: "Measured visibility unavailable" }
        },
        computed: {
            promptText() {
                return String((this.row && (this.row.tracked_text || this.row.text)) || "")
            },
            // the prompt detail of the AI Suite resolves a prompt by its text, so the tracked
            // spelling is what we hand over; the first platform with answers opens by default
            answersRoute() {
                const query = { prompt: this.promptText }
                const platforms = this.row && Array.isArray(this.row.platforms) ? this.row.platforms : []
                const cited = this.row && Array.isArray(this.row.cited_platforms) ? this.row.cited_platforms : []
                const platform = cited[0] || platforms[0]
                if (platform) query.platform = platform
                return { path: "/ai-suite/prompt-detail/" + this.projectId, query }
            },
            promptsRoute() {
                return { path: "/ai-suite/project/" + this.projectId + "/prompts" }
            },
            answersLine() {
                const platforms = Array.isArray(this.row.platforms) ? this.row.platforms.map(platformLabel) : []
                let line = this.row.answers + " answer" + (this.row.answers === 1 ? "" : "s") + " in " + this.days + " days"
                if (platforms.length) line += " · " + platforms.join(", ")
                return line
            },
            citedTitle() {
                const list = Array.isArray(this.row.cited_platforms) ? this.row.cited_platforms.map(platformLabel) : []
                if (!this.row.cited) return "No AI answer to this prompt cited this URL in the last " + this.days + " days."
                return "AI answers that cite this URL as a source" + (list.length ? " (" + list.join(", ") + ")" : "")
            },
            mentionedTitle() {
                const list = Array.isArray(this.row.mentioned_platforms) ? this.row.mentioned_platforms.map(platformLabel) : []
                if (!this.row.mentioned) return "Your brand was not mentioned in any AI answer to this prompt in the last " + this.days + " days."
                return "AI answers that mention your brand" + (list.length ? " (" + list.join(", ") + ")" : "")
            }
        }
    }
</script>

<style lang="scss" scoped>
    /* colours chosen for WCAG 2.1 AA text contrast (>= 4.5:1) on their backgrounds */
    .cm {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12px;
        color: #2d5a54;
        min-width: 0;
    }
    .cm__muted {
        color: #3d6d66;
        display: inline-flex;
        align-items: center;
    }
    .cm__stats {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .cm__stat {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        border-radius: 999px;
        padding: 3px 10px;
        border: 1px solid #d4ede9;
        background: #ffffff;
        color: #3d6d66;
        white-space: nowrap;
    }
    .cm__stat ::v-deep .v-icon {
        color: inherit;
    }
    .cm__stat--good {
        border-color: #b9e5dd;
        background: #e0f5f2;
        color: #00695c;
    }
    .cm__stat--warn {
        border-color: #f3e2b6;
        background: #fdf6e4;
        color: #8a5200;
    }
    .cm__stat--bad {
        border-color: #f2cccc;
        background: #fceaea;
        color: #b32d2d;
    }
    .cm__platforms {
        color: #3d6d66;
        line-height: 1.4;
    }
    .cm__link {
        color: #007a68;
        font-weight: 600;
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 2px;
        white-space: nowrap;
        width: fit-content;
    }
    .cm__link:hover {
        text-decoration-thickness: 2px;
    }
    .cm__link:focus-visible {
        outline: 2px solid #0d2622;
        outline-offset: 2px;
        border-radius: 3px;
    }
</style>
